"use client"

import * as React from "react";

import {useEffect, useState} from "react";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {PortableTextBlock} from "sanity";
import Image from "next/image";
import {GROQImage} from "@/sanity/lib/definitions";
import {motion} from "framer-motion";
import ScrollingBanner from "@/components/global/ui/ScrollingBanner";
import DecorationLine from "@/components/global/ui/DecorationLine";
import {Carousel, CarouselContent, CarouselItem, type CarouselApi} from "@/shadcn/ui/carousel";

export default function NumbersScroll({
  numbersScroll,
  numbersText,
  numbersItems,
  className,
}: {
  numbersScroll?: string;
  numbersText?: PortableTextBlock[];
  numbersItems: {
    title: PortableTextBlock[];
    image: GROQImage;
    number: {
      numberPrefix?: string;
      number: string;
      numberSuffix?: string;
    };
    text: PortableTextBlock[];
  }[];
  className?: string;
}) {
  const rowRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const setRowRef = (i: number) => (el: HTMLDivElement | null) => {
    rowRefs.current[i] = el;
  };

  React.useEffect(() => {
    const calcActive = () => {
      const stickyTop = window.innerHeight * 0.3;
      const threshold = 50;

      for (let i = rowRefs.current.length - 1; i >= 0; i--) {
        const el = rowRefs.current[i];
        if (!el) continue;

        const r = el.getBoundingClientRect();
        if (Math.abs(r.top - stickyTop) < threshold) {
          if (i !== activeIndex) {
            setActiveIndex(i);
          }
          break;
        }
      }
    };

    const onScroll = () => requestAnimationFrame(calcActive);
    calcActive();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [activeIndex]);


  // carousel state for mobile
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  if (!numbersItems || numbersItems.length === 0) return null;

  return (
    <section className={cn(
      "relative pb-[12rem] lg:pb-[25rem]",
      className
    )}>
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="left"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="right"
      />

      <div className="relative">
        {numbersScroll &&
          <ScrollingBanner
            text={numbersScroll}
          />
        }

        <div className="relative pt-[12rem] lg:pt-[20rem]">
          <DecorationLine
            className="decorationLineTop top-0 left-0"
            orientation="horizontal"
          />

          <div className="container relative">
            {numbersItems && (
              <div className="flex flex-col items-start sm:items-center gap-[3rem] lg:gap-[7rem]">
                {numbersText && (
                  <div className="richtext richtext-start sm:richtext-center prose-p:font-geistmono prose-p:text-[#202223]/20 prose-p:text-[2.25rem] lg:prose-p:text-[3rem]">
                    <PortableText
                      value={numbersText}
                      components={richTextComponent}
                    />
                  </div>
                )}

                {/* desktop */}
                {/* first layer for dark numbers + height */}
                <div className="w-[min(187.75rem,100%)] hidden lg:flex flex-col gap-[1rem] relative">
                  {numbersItems.map((item, i) => (
                    <div
                      key={i}
                      ref={setRowRef(i)}
                      className={cn(
                        "flex items-center justify-between gap-[1rem]"
                      )}
                    >
                      {/* left side text */}
                      <div className="w-[53.5rem] overflow-clip opacity-0">
                        {item?.title && (
                          <div className="richtext prose-headings:text-[#202223] prose-headings:text-[7rem] prose-headings:leading-none prose-headings:py-[1rem] prose-p:w-[min(51.875rem,100%)]">
                            <PortableText
                              value={item.title}
                              components={richTextComponent}
                            />
                          </div>
                        )}
                      </div>

                      {/* middle image + number */}
                      <div className="w-[62rem] relative">
                        {item?.image && (
                          <Image
                            className="!w-full h-auto [clip-path:polygon(0_0,calc(100%-3.5rem)_0,100%_3.5rem,100%_100%,0_100%)] opacity-0"
                            src={item.image}
                            alt=""
                          />
                        )}

                        <p className="top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 absolute font-medium flex items-start leading-none transition-colors text-black/10 mb-0">
                          {item?.number?.numberPrefix && (
                            <span className="text-[10rem]">{item.number.numberPrefix}</span>
                          )}
                          {item?.number?.number && (
                            <span className="text-[23rem]">{item.number.number}</span>
                          )}
                          {item?.number?.numberSuffix && (
                            <span className="text-[10rem]">{item.number.numberSuffix}</span>
                          )}
                        </p>
                      </div>

                      {/* right side text */}
                      <div className="w-[53.5rem] overflow-clip opacity-0">
                        <div className="richtext prose-headings:text-[#202223] prose-headings:text-[7rem] prose-p:w-[min(51.875rem,100%)]">
                          {item?.text && (
                            <PortableText
                              value={item.text}
                              components={richTextComponent}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* second layer for animation */}
                  <div className="absolute top-0 left-0 w-full h-full">
                    <div className="sticky top-[30%] w-full">
                      {numbersItems.map((item, i) => (
                        <div
                          key={i}
                          className={cn(
                            "flex items-center justify-between gap-[1rem] w-full",
                            i === 0 ? "relative" : "absolute top-0 left-0"
                          )}
                        >
                        {/* left side text */}
                        <div className="w-[53.5rem] overflow-clip">
                          <motion.div
                            initial={{ y: '100%' }}
                            animate={i === activeIndex ? { y: 0 } : (i < activeIndex ? { y: '-100%' } : { y: '100%' })}
                            transition={{ type: "spring", stiffness: 120, damping: 18 }}
                            className="richtext prose-headings:text-[#202223] prose-headings:text-[7rem] prose-headings:leading-none prose-headings:py-[1rem] prose-p:w-[min(51.875rem,100%)]"
                          >
                            {item?.title && (
                              <PortableText
                                value={item.title}
                                components={richTextComponent}
                              />
                            )}
                          </motion.div>
                        </div>

                        {/* middle image + number */}
                        <div className="w-[62rem]">
                          <div className="relative overflow-clip [clip-path:polygon(0_0,calc(100%-3.5rem)_0,100%_3.5rem,100%_100%,0_100%)]">
                            {item?.image && (
                              <motion.div
                                initial={{ y: '-100%' }}
                                animate={i === activeIndex ? { y: 0 } : (i < activeIndex ? { y: '100%' } : { y: '-100%' })}
                                transition={{ type: "spring", stiffness: 120, damping: 18 }}
                              >
                                <Image
                                  className="!w-full"
                                  src={item.image}
                                  alt=""
                                />
                              </motion.div>
                            )}

                            <div className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 overflow-clip pointer-events-none">
                              <motion.p
                                initial={{ y: '100%', opacity: 0 }}
                                animate={i === activeIndex ? { y: 0, opacity: 1 } : (i < activeIndex ? { y: '-100%', opacity: 0 } : { y: '100%', opacity: 0 })}
                                transition={{ type: "spring", stiffness: 120, damping: 18 }}
                                className="font-medium flex items-start leading-none text-[#D9F9B9] mb-0"
                              >
                                {item?.number?.numberPrefix && (
                                  <span className="text-[10rem]">{item.number.numberPrefix}</span>
                                )}
                                {item?.number?.number && (
                                  <span className="text-[23rem]">{item.number.number}</span>
                                )}
                                {item?.number?.numberSuffix && (
                                  <span className="text-[10rem]">{item.number.numberSuffix}</span>
                                )}
                              </motion.p>
                            </div>
                          </div>
                        </div>

                        {/* right side text */}
                        <div className="w-[53.5rem] overflow-clip">
                          <motion.div
                            initial={{ y: '100%' }}
                            animate={i === activeIndex ? { y: 0 } : (i < activeIndex ? { y: '-100%' } : { y: '100%' })}
                            transition={{ type: "spring", stiffness: 120, damping: 18 }}
                            className="richtext prose-headings:text-[#202223] prose-headings:text-[7rem] prose-p:w-[min(51.875rem,100%)]"
                          >
                            {item?.text && (
                              <PortableText
                                value={item.text}
                                components={richTextComponent}
                              />
                            )}
                          </motion.div>
                        </div>
                      </div>
                      ))}
                    </div>
                  </div>
                </div>
                {/* end desktop */}

                {/* start mobile */}
                <div className="lg:hidden w-full flex flex-col items-center">
                  <Carousel
                    className="w-full [&_>_div]:!overflow-visible"
                    setApi={setApi}
                  >
                    <CarouselContent className="-ml-[2rem]">
                      {numbersItems.map((item, index) => (
                        <CarouselItem
                          key={index}
                          className="pl-[2rem] basis-[42rem] sm:basis-[60rem] flex flex-col w-full"
                        >
                          <div className="relative w-full mb-[2rem]">
                            <Image
                              className="!w-full h-auto [clip-path:polygon(0_0,calc(100%-3.5rem)_0,100%_3.5rem,100%_100%,0_100%)]"
                              src={item.image}
                              alt=""
                            />

                            <p className="absolute left-[2.5rem] bottom-[2.5rem] font-medium flex items-start leading-none text-[#D9F9B9] mb-0">
                              {item?.number?.numberPrefix && (
                                <span className="text-[5rem] sm:text-[7rem]">{item.number.numberPrefix}</span>
                              )}
                              {item?.number?.number && (
                                <span className="text-[10rem] sm:text-[16rem]">{item.number.number}</span>
                              )}
                              {item?.number?.numberSuffix && (
                                <span className="text-[5rem] sm:text-[7rem]">{item.number.numberSuffix}</span>
                              )}
                            </p>
                          </div>

                          {item?.title && (
                            <div className="richtext richtext-start prose-headings:text-[#202223]/80 prose-headings:text-[4rem] sm:prose-headings:text-[4rem] mb-[1rem]">
                              <PortableText
                                value={item.title}
                                components={richTextComponent}
                              />
                            </div>
                          )}

                          {item?.text && (
                            <div className="richtext richtext-start prose-headings:text-[#202223]/80 prose-headings:text-[4rem] sm:prose-headings:text-[4rem]">
                              <PortableText
                                value={item.text}
                                components={richTextComponent}
                              />
                            </div>
                          )}
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </Carousel>

                  <div className={cn(
                    "w-full sm:w-auto flex items-center justify-between sm:justify-center gap-[2.25rem] mt-[2.5rem] px-[1.25rem] py-[1.125rem] relative border-[2px] border-white rounded-full",
                    "after:absolute after:-top-[1px] after:-left-[1px] after:-right-[1px] after:-bottom-[1px] after:border after:border-[#D3D3D2] after:rounded-full after:pointer-events-none"
                  )}>
                    <button
                      onClick={() => api?.scrollPrev()}
                      className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
                      disabled={!api || current === 0}
                      aria-label="Previous slide"
                    >
                      <svg viewBox="0 0 12 20" xmlns="http://www.w3.org/2000/svg" className="w-[1.25rem] h-auto flex-shrink-0 -translate-x-[.125rem]">
                        <path d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z" fill="currentColor"/>
                      </svg>
                    </button>

                    <div className="sm:w-[40rem] h-[.5rem] grow sm:grow-0 flex gap-[1rem] px-[.5rem]">
                      {numbersItems.map((_, index) => (
                        <div
                          key={index}
                          className={cn(
                            "grow h-full transition-colors",
                            index === current
                              ? "bg-[#202223]"
                              : "bg-[#202223]/20"
                          )}
                        />
                      ))}
                    </div>

                    <button
                      onClick={() => api?.scrollNext()}
                      className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
                      disabled={!api || current === numbersItems.length - 1}
                      aria-label="Next slide"
                    >
                      <svg viewBox="0 0 12 20" xmlns="http://www.w3.org/2000/svg" className="rotate-180 w-[1.25rem] h-auto flex-shrink-0 translate-x-[.125rem]">
                        <path d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z" fill="currentColor"/>
                      </svg>
                    </button>
                  </div>
                </div>
                {/* end mobile */}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
